import _get from 'lodash/get'
import { AxiosError } from 'axios'
import {
  InfiniteQueryObserverResult,
  QueryFunctionContext,
  QueryObserverResult,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query'

import http from '../helpers/http'
import { filterDealPatchResponse, getDeal, getDealProfile } from '../helpers/utils'
import {
  DealsVars,
  IPipeline,
  ICollectionDealsData,
  ICollectionDeal,
  IDeal,
  IRawDeal,
  IPipelineStage,
  ICreateDeal,
} from '../types/deals-types'
import { IDealStatistics, ITimelinesResults } from '../types'

export const dealProfileKey = 'deal-profile'
export const collectionDealKey = 'collection-deal'
export const dealPipelineKey = 'deal-pipelines'
export const collectionDealsPagedKey = 'collection-deals-paged'
export const DealsStatistics = 'deals-statistics'
export const dealTimelinePagedKey = 'deal-timeline-paged'
export const dealTimelineKey = 'deal-timeline'

export function useCollectionDealQuery(id: number): QueryObserverResult<ICollectionDeal> {
  return useQuery({
    queryKey: [collectionDealKey, { id }],
    queryFn: ({ signal }) =>
      http
        .get(`collection/deals/${id}`, {
          signal,
        })
        .then((res) => res?.data),
    staleTime: Infinity,
    notifyOnChangeProps: 'tracked',
  })
}

export function useCollectionDealsInfiniteQuery(
  { itemsPerPage = 10, ownerId, status, pipeline, pipelineStage, sortName, sortDirection } = {} as DealsVars
): InfiniteQueryObserverResult<ICollectionDealsData> {
  const queryClient = useQueryClient()
  const queryFn = ({ pageParam, signal }: QueryFunctionContext) =>
    http
      .get('/collection/deals', {
        params: {
          offset: pageParam?.offset,
          itemsPerPage,
          'owner.id': ownerId,
          status,
          'pipeline.id': pipeline,
          'pipelineStage.id': pipelineStage,
          [`sort[${sortName}]`]: sortDirection,
        },
        signal,
      })
      .then((res) => res?.data)

  const getNextPageParam = (lastResult: ICollectionDealsData) => {
    const { data, offset, itemsPerPage } = lastResult
    return data && data.length === itemsPerPage ? { offset: offset + itemsPerPage, itemsPerPage } : undefined
  }

  const config: UseInfiniteQueryOptions<ICollectionDealsData> = {
    queryKey: [collectionDealsPagedKey, { ownerId, status, pipeline, pipelineStage, sortName, sortDirection }],
    queryFn,
    getNextPageParam,
    onSuccess(data) {
      for (const page of data?.pages ?? []) {
        for (const prospect of page?.data ?? []) {
          if (prospect) queryClient.setQueryData([collectionDealKey, { id: prospect.id }], prospect)
        }
      }
    },
  }

  return useInfiniteQuery(config)
}

export function useDealPipelineQuery(): QueryObserverResult<IPipeline[]> {
  return useQuery({
    queryKey: [dealPipelineKey],
    queryFn: ({ signal }) =>
      http.get('/deal-pipelines', { signal }).then((res) =>
        res?.data.map((pipeline: IPipeline) => {
          pipeline.stages = pipeline?.stages?.sort(
            (a: IPipelineStage, b: IPipelineStage) => a.stageOrder - b.stageOrder
          )
          return pipeline
        })
      ),
    staleTime: Infinity,
    notifyOnChangeProps: 'tracked',
  })
}

export function useDealsPagedRemoveQueries(): () => void {
  const queryClient = useQueryClient()
  return () => queryClient.removeQueries(collectionDealsPagedKey)
}

export function useGetRawDealById(id: string): QueryObserverResult<Partial<IDeal>> {
  const config: UseQueryOptions<Partial<IDeal>> = {
    queryKey: [dealProfileKey],
    queryFn: ({ signal }) => http.get(`/deals/${id}`, { signal }).then((res) => getDeal(res?.data)),
    staleTime: 10000,
    notifyOnChangeProps: 'tracked',
  }
  return useQuery(config)
}

export function useDealProfile(
  id: string,
  configOptions?: UseQueryOptions<Partial<IDeal>>
): QueryObserverResult<Partial<IDeal>> {
  const queryFn = ({ signal }: { signal?: AbortSignal }): Promise<Partial<IDeal>> =>
    Promise.all([http.get(`/deals/${id}`, { signal }), http.get(`/deals/metadata`, { signal })]).then((res) =>
      getDealProfile({ dealRawData: _get(res, '[0].data'), dealMetadata: _get(res, '[1].data') })
    )
  const config: UseQueryOptions<Partial<IDeal>> = {
    queryKey: [dealProfileKey, { id }],
    queryFn,
    staleTime: 10000,
    notifyOnChangeProps: 'tracked',
    ...configOptions,
  }
  return useQuery(config)
}

export function usePatchDealProfile(id: string): UseMutationResult<IRawDeal, AxiosError, Partial<IRawDeal>> {
  const queryClient = useQueryClient()
  const mutationFn = (data: Partial<IRawDeal>): Promise<IRawDeal> =>
    http.patch(`/deals/${id}`, data, {}).then((res) => res?.data)

  return useMutation(mutationFn, {
    onSuccess(responseData) {
      const cachedData = queryClient.getQueryData([dealProfileKey, { id }]) as IDeal
      queryClient.setQueryData([dealProfileKey, { id }], filterDealPatchResponse({ responseData, cachedData }))
    },
  })
}

export function usePostDeal(): UseMutationResult<ICreateDeal, AxiosError, Partial<ICreateDeal>> {
  const queryClient = useQueryClient()
  const mutationFn = (data: Partial<ICreateDeal>): Promise<ICreateDeal> =>
    http.post(`/deals`, data).then((res) => res?.data)

  return useMutation(mutationFn, {
    async onSuccess(deal) {
      queryClient.setQueryData([collectionDealKey, { id: deal.id }], deal)
    },
  })
}

export function useDealStatistics({
  prospectId,
  organizationId,
}: {
  prospectId?: string
  organizationId?: string
}): QueryObserverResult<IDealStatistics> {
  return useQuery({
    queryKey: [DealsStatistics, { prospectId, organizationId }],
    queryFn: ({ signal }) =>
      http.get(`statistics/deals`, {
        params: { prospect: prospectId, organization: organizationId },
        signal,
      }),
    staleTime: 10000,
    notifyOnChangeProps: 'tracked',
  })
}

export function useDealByIdFetch(): (id: string) => Promise<Partial<IDeal>> {
  const queryClient = useQueryClient()

  return (id: string) =>
    queryClient.fetchQuery({
      queryKey: [dealProfileKey, { id }],
      queryFn: ({ signal }: { signal?: AbortSignal }): Promise<Partial<IDeal>> =>
        Promise.all([http.get(`/deals/${id}`, { signal }), http.get(`/deals/metadata`, { signal })]).then((res) =>
          getDealProfile({ dealRawData: _get(res, '0.data'), dealMetadata: _get(res, '1.data') })
        ),
    })
}

export function useDealTimelineInfiniteQuery(
  { itemsPerPage = 10, id, filterBy } = {} as { itemsPerPage: number; id: string; filterBy: string }
): InfiniteQueryObserverResult<ITimelinesResults> {
  const queryClient = useQueryClient()

  const getNextPageParam = (lastResult: ITimelinesResults) => {
    const { data, offset, itemsPerPage } = lastResult
    return data && data.length === itemsPerPage ? { offset: offset + itemsPerPage, itemsPerPage } : undefined
  }

  const config: UseInfiniteQueryOptions<ITimelinesResults> = {
    queryKey: [dealTimelinePagedKey, { id, filterBy }],
    queryFn: ({ pageParam, signal }) =>
      http
        .get(`/deals/${id}/timeline`, {
          params: {
            offset: pageParam?.offset,
            itemsPerPage,
            filterBy,
          },
          signal,
        })
        .then((res) => res?.data),
    getNextPageParam,
    onSuccess(data) {
      for (const page of data?.pages ?? []) {
        for (const timeline of page?.data ?? []) {
          if (timeline) queryClient.setQueryData([dealTimelineKey, { id: timeline.id }], timeline)
        }
      }
    },
  }

  return useInfiniteQuery(config)
}
